import { useMutation, useQuery } from "@apollo/client";
import React, { useState, useEffect, useRef } from "react";
import sfdaMedicine from "../../../gql/sfdaMedicine";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Pagination from "../../../components/Pagination";
import { useTranslation } from "react-i18next";
import TableSkeletonLoader from "../../../common/TableLoading";
import AppTable from "../../../shared/AppTable";
import AppButton from "../../../shared/AppButton";
import AddIcon from "@mui/icons-material/Add";
import SearchInput from "../../../components/SearchInput";
import ConformationModal from "../../../components/ConformationModal";
import CustomFilterInput from "../../../shared/CustomFilterInput";
import { FaFilter, FaUndo } from "react-icons/fa";
import axios from "axios";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { API_BASE_URL } from "../../../config/apiConfig";

function SfdaMedicineList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [limit, setLimit] = useState(15);
  const [pageNo, setPageNo] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [search, setSearch] = useState("");
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const uploadInputRef = useRef(null);

  const { loading, error, data, refetch } = useQuery(
    sfdaMedicine.GET_SFDA_MEDICINES,
    {
      variables: { page: pageNo, limit: limit, search: search },
      fetchPolicy: "network-only",
    }
  );

  const [deleteMedicine] = useMutation(sfdaMedicine.DELETE_SFDA_MEDICINE, {
    onCompleted: () => {
      toast.success(t("Successfully Deleted!"));
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  useEffect(() => {
    if (error) {
      toast.error(error.message);
    }
  }, [error]);

  const onPageChange = (currentPage) => {
    setPageNo(currentPage);
  };

  const onClickDelete = (item) => {
    setCurrentItem(item);
    setShowDeleteAlert(true);
  };

  const handleDelete = () => {
    deleteMedicine({ variables: { id: currentItem.id } });
    setShowDeleteAlert(false);
  };

  const onClickEdit = (item) => {
    navigate("/super-admin/sfda-medicine/form", { state: { item } });
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token")?.replace(/"/g, "");
    return token ? { Authorization: "Bearer " + token } : {};
  };

  const downloadSfdaExcel = async () => {
    setExporting(true);
    try {
      const queueResponse = await axios.post(
        API_BASE_URL + "/sfda/medication/export",
        null,
        {
          headers: getAuthHeaders(),
          params: search ? { search } : undefined,
        }
      );
      const jobId = queueResponse.data?.data?.job_id;
      if (!jobId) {
        throw new Error("The SFDA export job could not be created");
      }

      toast.success(t("SFDA Excel export queued. It will download when ready."));
      for (let attempt = 0; attempt < 900; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
        const statusResponse = await axios.get(
          API_BASE_URL + "/sfda/medication/export/" + jobId,
          { headers: getAuthHeaders() }
        );
        const job = statusResponse.data?.data;
        if (job?.status === "completed") {
          const fileResponse = await axios.get(
            API_BASE_URL + "/sfda/medication/export/" + jobId + "/file",
            { headers: getAuthHeaders(), responseType: "blob" }
          );
          const url = window.URL.createObjectURL(new Blob([fileResponse.data]));
          const link = document.createElement("a");
          link.href = url;
          link.download = "SFDA_Medicines.xlsx";
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          toast.success(
            t("SFDA Excel downloaded successfully ({{count}} rows)", {
              count: job.exported_rows || 0,
            })
          );
          return;
        }
        if (job?.status === "failed") {
          throw new Error(job.error || "SFDA Excel export failed");
        }
      }
      throw new Error("SFDA Excel export is taking longer than expected");
    } catch (requestError) {
      toast.error(requestError.message || t("Unable to export SFDA Excel"));
    } finally {
      setExporting(false);
    }
  };
  const uploadSfdaExcel = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error(t("Please select a valid .xlsx file"));
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(
        API_BASE_URL + "/sfda/medication/import",
        formData,
        { headers: getAuthHeaders() }
      );
      const result = response.data?.data;
      toast.success(
        t("Updated {{count}} SFDA medicine(s) successfully", {
          count: result?.updatedRows || 0,
        })
      );
      refetch();
    } catch (requestError) {
      const responseData = requestError.response?.data;
      const issues = responseData?.data?.issues || [];
      const issueSummary = issues
        .slice(0, 3)
        .map((issue) =>
          "Row " +
          issue.row +
          (issue.column ? " (" + issue.column + ")" : "") +
          ": " +
          issue.message
        )
        .join(" | ");
      toast.error(issueSummary || responseData?.error || t("Unable to upload SFDA Excel"), {
        duration: 8000,
      });
    } finally {
      setImporting(false);
    }
  };
  const columns = [
    {
      header: t("Register Number"),
      field: "registerNumber",
      render: (row) => <span className="font-medium">{row.registerNumber}</span>,
    },
    { header: t("Trade Name"), field: "tradeName" },
    { header: t("Scientific Name"), field: "scientificName" },
    { header: t("Marketing Company"), field: "marketingCompany" },
    { header: t("Public Price"), field: "publicPrice" },
    // {
    //   header: t("Status"),
    //   render: (row) => (
    //     <span
    //       className={`px-2 py-1 rounded-full text-xs font-semibold ${row.marketingStatus === "Active"
    //         ? "bg-green-100 text-green-800"
    //         : "bg-gray-100 text-gray-800"
    //         }`}
    //     >
    //       {t(row.marketingStatus)}
    //     </span>
    //   ),
    // },
  ];

  return (
    <div className="superGit_container !p-0">
      <div className="flex justify-between items-start flex-col md:flex-row gap-2 mb-2">
        <div className="w-full sm:w-[calc(100%-150px)]">
          <span className="font-semibold text-black/70 text-lg ">
            {t("SFDA MEDICINES")}
            <span className=" text-lg ml-1">
              ({data?.getSfdaMedicines?.pageInfo?.totalCount || 0}{" "}
              <span className="text-xs font-bold italic">{t("Entries")}</span>)
            </span>
          </span>
        </div>
        <div className="w-full flex gap-1.5 flex-col md:flex-row justify-end">
          <input
            ref={uploadInputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={uploadSfdaExcel}
          />
          <AppButton
            label={importing ? t("Uploading...") : t("Upload Excel")}
            startIcon={<UploadFileIcon />}
            disabled={importing || exporting}
            onClick={() => uploadInputRef.current?.click()}
          />
          <AppButton
            label={exporting ? t("Downloading...") : t("Download Excel")}
            startIcon={<DownloadIcon />}
            disabled={importing || exporting}
            onClick={downloadSfdaExcel}
          />
          <AppButton
            label={t("Create Medicine")}
            startIcon={<AddIcon />}
            disabled={importing || exporting}
            onClick={() => navigate("/super-admin/sfda-medicine/form")}
          />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-2 mb-2">
        <div className="flex items-center">
          <div className="w-full flex flex-col sm:flex-row gap-x-1.5 gap-y-1.5 justify-end">
            <div className="w-[200px]">
              <CustomFilterInput
                label={t("Search Medicines")}
                name="search"
                placeholder={t("Search by name, register number...")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2 mt-[19px]">
              <AppButton
                // label={t("Reset")}
                variant="reset"
                className="!bg-slate-200"
                onClick={() => {
                  setSearchTerm("");
                  setSearch("");
                  setPageNo(1);
                }}
                startIcon={<FaUndo className="!text-[12px] " />}
              />
              <AppButton
                // label={t("Apply Filter")}
                onClick={() => {
                  setSearch(searchTerm);
                  setPageNo(1);
                }}
                startIcon={<FaFilter className="!text-[12px]" />}
              />
            </div>
          </div>
        </div>
      </div>

      <AppTable
        columns={columns}
        data={data?.getSfdaMedicines?.medicines}
        loading={loading}
        onEdit={onClickEdit}
        // onDelete={onClickDelete}
        LoaderComponent={<TableSkeletonLoader columnCount={6} rowCount={10} />}
      />

      <div className="mt-4">
        <Pagination
          currentPage={pageNo}
          totalPages={data?.getSfdaMedicines?.pageInfo?.totalPages || 1}
          onPageChange={onPageChange}
        />
      </div>

      <ConformationModal
        open={showDeleteAlert}
        setOpen={setShowDeleteAlert}
        onDelete={handleDelete}
      />
    </div>
  );
}

export default SfdaMedicineList;
